import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../redux-store";
import { updateCustomerDeliveryLocation } from "../ducks/data/economics/customer-delivery-locations";
import { CustomerDeliveryLocation, Place } from "dora-contracts";
import { DoraButton } from "dora-shared/frontend";
import NewPlaceSearch, { AddressChange } from "./NewPlaceSearch";

const EditDeliveryAddressDialog = (props: {
  location: CustomerDeliveryLocation;
  onClose: () => void;
}) => {
  const { location, onClose } = props;
  const { t } = useTranslation(["invoicing", "shipmentModal", "components"]);
  const dispatch = useAppDispatch();
  const [name, setName] = useState(location.name ?? "");
  const [address, setAddress] = useState(location.address ?? "");
  const [postalCode, setPostalCode] = useState(location.postalCode ?? "");
  const [city, setCity] = useState(location.city ?? "");
  const [country, setCountry] = useState(location.country ?? "");
  // Set when the user picks a new address via the place search; carries the
  // freshly geocoded coordinates so they get persisted on save.
  const [resolvedPlace, setResolvedPlace] = useState<Place | null>(null);
  const [saving, setSaving] = useState(false);

  const onPlaceSelected = (addressChange: AddressChange | null) => {
    if (!addressChange) {
      setResolvedPlace(null);
      return;
    }
    // Prefill the editable fields from the search result...
    setAddress(addressChange.address ?? "");
    setPostalCode(addressChange.postcode ?? "");
    setCity(addressChange.city ?? "");
    setCountry(addressChange.country ?? "");
    // ...and remember the full place so the new coordinates are saved too.
    setResolvedPlace({
      address: addressChange.address ?? null,
      postcode: addressChange.postcode ?? null,
      city: addressChange.city ?? null,
      country: addressChange.country ?? null,
      placeName: addressChange.placeName ?? null,
      coord: {
        lon: addressChange.coord.lon,
        lat: addressChange.coord.lat,
      },
      ...(addressChange.googlePlaceId && {
        googlePlaceId: addressChange.googlePlaceId,
      }),
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateCustomerDeliveryLocation({
          clientId: location.clientId,
          locationId: location.id,
          changes: {
            name,
            address: address || null,
            postalCode: postalCode || null,
            city: city || null,
            country: country || null,
            ...(resolvedPlace && { resolvedPlace }),
          },
        })
      ).unwrap();
      onClose();
    } catch {
      // The updateCustomerDeliveryLocation thunk has already reported it and shown the error toast.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("invoicing:deliveryAddressDialog.editTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <NewPlaceSearch onPlaceSelected={onPlaceSelected} placeError={false} />
          <TextField
            label={t("invoicing:deliveryAddressDialog.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("shipmentModal:inputs.searchPlaceAddressPlaceholder")}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("shipmentModal:inputs.searchPlaceZipPlaceholder")}
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("shipmentModal:inputs.searchPlaceCityPlaceholder")}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            fullWidth
          />
          <TextField
            label={t("shipmentModal:inputs.searchPlaceCountryPlaceholder")}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <DoraButton variant="ghost" onClick={onClose} disabled={saving}>
          {t("components:buttonLabels.cancel")}
        </DoraButton>
        <DoraButton variant="primary-filled" onClick={onSave} disabled={saving}>
          {t("components:buttonLabels.save")}
        </DoraButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditDeliveryAddressDialog;
